import {
  PayeeRequirement,
  RequirementStatus,
  RequirementType
} from "@wingspanhq/wingspan-api-sdk";
import {
  EmployeeTaxFormsMetadata,
  getEmployeeTaxFormsFormIds
} from "../helpers/getEmployeeTaxFormsProgress";
import { EmployeeTaxFormsRequirementChecklist } from "../../../../../EmployeeTaxForms/components/EmployeeTaxFormsRequirementChecklist";
import { getRequirementDefinitionTableCellProps } from "../../../../../RequirementDefinitions/utils";
import {
  CellsConfig,
  ContextItem,
  RequirementTableCallback
} from "../../../../types";
import { getEmployeeTaxFormsRequirementName } from "../helpers/getEmployeeTaxFormsRequirementName";
import { getEmployeeTaxFormsStatusCell } from "../helpers/getEmployeeTaxFormsStatusCell";

/**
 * Contractor-facing config (RequirementContextType.Payer — the context naming
 * is from the contractor's perspective: "requirements my payer imposed").
 */
export const usePayerEmployeeTaxFormsConfig = (
  _callback: RequirementTableCallback
): CellsConfig<PayeeRequirement, ContextItem, EmployeeTaxFormsMetadata> => {
  return {
    // EmployeeTaxForms completion is data-source-driven: trust dataSourceStatus
    // (all forms submitted) over a stale requirement.status/payer override.
    isActionRequired: requirement =>
      (requirement.dataSourceStatus ?? requirement.status) !==
      RequirementStatus.COMPLETED,

    requirementName: getEmployeeTaxFormsRequirementName,

    requirementStatusAlert: requirement => {
      if (
        (requirement.dataSourceStatus ?? requirement.status) !==
        RequirementStatus.COMPLETED
      ) {
        return {
          theme: "warning",
          title: "Action required",
          children:
            "Complete the tax onboarding forms in order to be eligible to receive payments from your client.",
          icon: "alert"
        };
      }
    },

    // The forms list lives inline on the requirement details page rather than
    // behind a "Start" toolbar action that routes to a separate screen. Each
    // row opens its own form guide and returns here after submit. `formIds`
    // comes straight off the requirement's own dataSourceMetadata (payments'
    // EmployeeTaxFormsRequirementService writes it on refresh) — no extra
    // lookup needed to discover which instances apply.
    requirementAdditionalContent: requirement => (
      <EmployeeTaxFormsRequirementChecklist
        formIds={getEmployeeTaxFormsFormIds(requirement.dataSourceMetadata)}
      />
    ),

    nameCell: requirement => ({
      text: getEmployeeTaxFormsRequirementName(requirement)
    }),

    typeCell: requirement =>
      getRequirementDefinitionTableCellProps({
        type: requirement.type as RequirementType
      }),

    statusCell: (requirement, _context, metadata) =>
      getEmployeeTaxFormsStatusCell(requirement, metadata)
  };
};
